import { useState, useEffect, useRef } from "react";

export interface UseOptionSelectionProps {
  questionId: string;
  isMultiSelect: boolean;
  currentAnswers?: string | string[];
  options: Array<{
    value: string;
    editable?: boolean;
    label: string;
    tooltip?: string;
  }>;
  onSelect: (questionId: string, value: string | string[]) => void;
}

export interface UseOptionSelectionReturn {
  // Selection state
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
  selectionOrder: string[];
  setSelectionOrder: React.Dispatch<React.SetStateAction<string[]>>;

  // Edit state
  editValues: { [key: string]: string };
  setEditValues: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  editing: string | null;
  setEditing: React.Dispatch<React.SetStateAction<string | null>>;

  // Modal state
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalConfig: {
    optionValue: string;
    label: string;
    placeholder: string;
  } | null;
  setModalConfig: React.Dispatch<
    React.SetStateAction<{
      optionValue: string;
      label: string;
      placeholder: string;
    } | null>
  >;

  // Animation state
  isRevealed: boolean;
  setIsRevealed: React.Dispatch<React.SetStateAction<boolean>>;

  // Ref
  inputRef: React.RefObject<HTMLInputElement>;

  // Handlers
  handleSelectOption: (optionValue: string, editable?: boolean) => void;
  handleModalConfirm: (value: string) => void;
  handleModalClose: () => void;
  handleConfirmMultiSelect: () => void;
}

export const useOptionSelection = ({
  questionId,
  isMultiSelect,
  currentAnswers,
  options,
  onSelect,
}: UseOptionSelectionProps): UseOptionSelectionReturn => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectionOrder, setSelectionOrder] = useState<string[]>([]);
  const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    optionValue: string;
    label: string;
    placeholder: string;
  } | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (currentAnswers) {
      const answers = Array.isArray(currentAnswers)
        ? currentAnswers
        : [currentAnswers];

      // Parse the current answers to extract selected options and edit values
      const parsedSelectedOptions: string[] = [];
      const parsedEditValues: { [key: string]: string } = {};

      answers.forEach((answer) => {
        if (answer.includes(":")) {
          // This is an edited value in format "value:customText"
          const [originalValue, customText] = answer.split(":", 2);

          parsedSelectedOptions.push(originalValue);
          parsedEditValues[originalValue] = customText;
        } else {
          // This is a regular value
          parsedSelectedOptions.push(answer);
        }
      });

      setSelectedOptions(parsedSelectedOptions);
      setSelectionOrder(parsedSelectedOptions); // Maintain the same order initially
      setEditValues(parsedEditValues);
    } else {
      setSelectedOptions([]);
      setSelectionOrder([]);
      setEditValues({});
    }
  }, [currentAnswers, questionId]);

  // Ensure options with edit values are selected in multi-select mode
  useEffect(() => {
    if (isMultiSelect) {
      const optionsWithEditValues = Object.keys(editValues).filter(
        (optionValue) =>
          editValues[optionValue] && editValues[optionValue].trim() !== "",
      );

      optionsWithEditValues.forEach((optionValue) => {
        if (!selectedOptions.includes(optionValue)) {
          setSelectedOptions((prev) => [...prev, optionValue]);
          setSelectionOrder((prev) =>
            prev.includes(optionValue) ? prev : [...prev, optionValue],
          );
        }
      });
    }
  }, [editValues, isMultiSelect, selectedOptions, selectionOrder]);

  const handleSelectOption = (optionValue: string, editable?: boolean) => {
    if (editable) {
      // For multi-select: if the option is already selected, deselect it
      if (isMultiSelect && selectedOptions.includes(optionValue)) {
        setSelectedOptions((prevSelected) =>
          prevSelected.filter((v) => v !== optionValue),
        );
        setSelectionOrder((prevOrder) =>
          prevOrder.filter((v) => v !== optionValue),
        );
        // Also remove the edit value
        setEditValues((prev) => {
          const newEditValues = { ...prev };

          delete newEditValues[optionValue];

          return newEditValues;
        });

        return;
      }

      // For multi-select: if the option has existing text but is not selected, just select it
      if (
        isMultiSelect &&
        editValues[optionValue] &&
        !selectedOptions.includes(optionValue)
      ) {
        setSelectedOptions([...selectedOptions, optionValue]);
        setSelectionOrder([...selectionOrder, optionValue]);

        return;
      }

      // Find the option to get label and tooltip
      const option = options.find((opt) => opt.value === optionValue);

      if (option) {
        // For multi-select, add the option immediately to preserve selection order
        if (isMultiSelect && !selectedOptions.includes(optionValue)) {
          setSelectedOptions([...selectedOptions, optionValue]);
          setSelectionOrder([...selectionOrder, optionValue]);
        }

        setModalConfig({
          optionValue,
          label: option.label,
          placeholder:
            option.tooltip ||
            `Please specify your answer for "${option.label}"`,
        });
        setModalOpen(true);
      }
    } else {
      setEditing(null);
      if (isMultiSelect) {
        setSelectedOptions((prevSelected) =>
          prevSelected.includes(optionValue)
            ? prevSelected.filter((v) => v !== optionValue)
            : [...prevSelected, optionValue],
        );
        setSelectionOrder((prevOrder) =>
          prevOrder.includes(optionValue)
            ? prevOrder.filter((v) => v !== optionValue)
            : [...prevOrder, optionValue],
        );
      } else {
        setSelectedOptions([optionValue]);
        onSelect(questionId, optionValue);
        // Track user action would go here if needed
      }
    }
  };

  const handleModalConfirm = (value: string) => {
    if (modalConfig) {
      const { optionValue } = modalConfig;
      const valueToSubmit = `${optionValue}:${value}`;

      if (!isMultiSelect) {
        setSelectedOptions([optionValue]);
        onSelect(questionId, valueToSubmit);
        // Track user action would go here if needed
      } else {
        // Ensure the option is selected when confirming modal
        if (!selectedOptions.includes(optionValue)) {
          setSelectedOptions([...selectedOptions, optionValue]);
          setSelectionOrder([...selectionOrder, optionValue]);
        }
        // Store the edit value
        setEditValues((prev) => ({ ...prev, [optionValue]: value }));
      }
    }
  };

  const handleModalClose = () => {
    // Don't remove the option if it has edit value (was confirmed)
    // Only remove if user actually canceled without entering text
    if (modalConfig && isMultiSelect && !editValues[modalConfig.optionValue]) {
      setSelectedOptions((prevSelected) =>
        prevSelected.filter((v) => v !== modalConfig.optionValue),
      );
      setSelectionOrder((prevOrder) =>
        prevOrder.filter((v) => v !== modalConfig.optionValue),
      );
    }
    setModalOpen(false);
    setModalConfig(null);
  };

  const handleConfirmMultiSelect = () => {
    // Use selectionOrder to maintain the chronological order of selection
    const valuesToSubmit = selectionOrder
      .filter((val) => selectedOptions.includes(val)) // Only include actually selected options
      .map((val) => {
        const opt = options.find((o) => o.value === val);

        if (opt?.editable && editValues[val] !== undefined) {
          return `${val}:${editValues[val]}`;
        }

        return val;
      });

    onSelect(questionId, valuesToSubmit);
    // Track user action would go here if needed
  };

  return {
    // Selection state
    selectedOptions,
    setSelectedOptions,
    selectionOrder,
    setSelectionOrder,

    // Edit state
    editValues,
    setEditValues,
    editing,
    setEditing,

    // Modal state
    modalOpen,
    setModalOpen,
    modalConfig,
    setModalConfig,

    // Animation state
    isRevealed,
    setIsRevealed,

    // Ref
    inputRef,

    // Handlers
    handleSelectOption,
    handleModalConfirm,
    handleModalClose,
    handleConfirmMultiSelect,
  };
};
